import { companySummaryUrl } from 'common/config/urls';
import { FirmCompany } from 'common/types/company';
import { getCompanyAliasLabel, getCompanyAliasPath } from 'layouts/FirmBaseLayout/companyAliasSearch';
import { ALPHA_CHARS, GROUP_CHARS } from './Home.constants';
import type { GridEntry } from './Home.props';

const toCompanyEntry = (company: FirmCompany): GridEntry => ({
  getUrl: firmSlug => companySummaryUrl(firmSlug, company.company_slug),
  key: company.company_slug,
  label: company.company_name,
  sortName: company.company_name,
});

// Every company when not searching; otherwise matching companies plus matching aliases, as the navbar does.
export const buildGridEntries = (companies: FirmCompany[], query: string): GridEntry[] => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return companies.map(toCompanyEntry);
  }

  const matches = (name: string) => name.toLowerCase().includes(q);
  return companies.flatMap(company => [
    ...(matches(company.company_name) ? [toCompanyEntry(company)] : []),
    ...(company.aliases ?? [])
      .filter(alias => matches(alias.name))
      .map(alias => ({
        getUrl: (firmSlug: string) => getCompanyAliasPath(alias, firmSlug, company.company_slug),
        key: `alias:${alias.id}`,
        label: getCompanyAliasLabel(alias, company.company_name),
        sortName: alias.name,
      })),
  ]);
};

export const groupAlphabetically = (entries: GridEntry[]) => {
  const groups: Record<string, GridEntry[]> = {};
  [...entries]
    .sort((a, b) => a.sortName.localeCompare(b.sortName))
    .forEach(entry => {
      const firstCharacter = entry.sortName.charAt(0).toUpperCase();
      (groups[ALPHA_CHARS.includes(firstCharacter) ? firstCharacter : '#'] ??= []).push(entry);
    });
  return GROUP_CHARS.filter(char => groups[char]).map(char => ({ char, entries: groups[char] }));
};
